'use client';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';
import { cardReset } from '../_shared/styles';

/**
 * Search-first hero block for the help-center landing page. Renders a
 * large title + search input; types-as-you-go and surfaces the
 * top-ranked ACTIVE help articles from
 * {@code POST /v1/public/{tenantId}/help-center/article/search}.
 * Submitting the form (Enter or the explicit button) navigates to a
 * results-page route — operators can override the URL template via
 * {@link HelpArticleSearchProps.resultsHrefTemplate}; the default
 * sends the user to {@code /help/search?q=&hellip;}.
 *
 * <p>Like {@code HelpArticleFeedbackForm}, this block POSTs directly
 * rather than consuming a runtime adapter — the help-center search
 * endpoint is unique to one block and a one-off Source would be more
 * ceremony than it's worth. When other search-first blocks land we
 * can extract a shared "TenantedSearch" primitive.
 */
export interface HelpArticleSearchProps {
  /** Server-injected tenant id. */
  tenantId?: number | string;
  /** Section title rendered above the input. */
  title?: string;
  /** Subhead under the title. */
  subtitle?: string;
  /** Placeholder text for the input. */
  placeholder?: string;
  /**
   * Whether to show inline live-suggestions under the input. Default
   * {@code true}; set false for a search-only hero (no autocomplete
   * UI).
   */
  showSuggestions?: boolean;
  /** Maximum suggestion rows. Default 6. */
  suggestionLimit?: number;
  /** Submit button label. */
  submitLabel?: string;
  /**
   * Href template for a clicked suggestion. Substitutes
   * {@code {slug}}. Default {@code /help/{slug}}.
   */
  articleHrefTemplate?: string;
  /**
   * Href template for the explicit submit (form submit). Substitutes
   * {@code {q}} with the encoded query. Default
   * {@code /help/search?q={q}}.
   */
  resultsHrefTemplate?: string;
  /** Override the API base URL. Defaults to the window origin. */
  apiBaseUrl?: string;
}

interface SuggestionRow {
  id?: number | string;
  slug?: string;
  title?: Record<string, string> | string;
  excerpt?: Record<string, string> | string;
}

function resolveBase(apiBaseUrl: string | undefined): string {
  if (apiBaseUrl && apiBaseUrl.trim()) return apiBaseUrl.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return '';
}

function localised(value: Record<string, string> | string | undefined): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  for (const v of Object.values(value)) {
    if (v != null && `${v}`.trim()) return `${v}`;
  }
  return '';
}

function fillTemplate(tpl: string, replacements: Record<string, string>): string {
  let out = tpl;
  for (const [key, value] of Object.entries(replacements)) {
    out = out.replace(`{${key}}`, value);
  }
  return out;
}

export function HelpArticleSearch({
  tenantId,
  title,
  subtitle,
  placeholder,
  showSuggestions = true,
  suggestionLimit = 6,
  submitLabel,
  articleHrefTemplate = '/help/{slug}',
  resultsHrefTemplate = '/help/search?q={q}',
  apiBaseUrl,
}: HelpArticleSearchProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const headingText = title ?? t('helpCenter.search.title');
  const placeholderText = placeholder ?? t('helpCenter.search.placeholder');
  const submitText = submitLabel ?? t('helpCenter.search.submit');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const safeLimit = useMemo(() => Math.max(1, Math.min(suggestionLimit, 20)), [suggestionLimit]);

  useEffect(() => {
    if (!showSuggestions) {
      setSuggestions([]);
      return;
    }
    if (tenantId == null || tenantId === '') return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const base = resolveBase(apiBaseUrl);
    const url = `${base}/v1/public/${encodeURIComponent(`${tenantId}`)}/help-center/article/search`;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ searchTerm: trimmed, maxResults: safeLimit }),
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = (await res.json()) as { resultSet?: SuggestionRow[] };
        if (controller.signal.aborted) return;
        setSuggestions(Array.isArray(json?.resultSet) ? json.resultSet : []);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Quietly clear — search-as-you-type errors shouldn't trip a
        // visible UI; the form-submit path still works.
        setSuggestions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [query, tenantId, safeLimit, showSuggestions, apiBaseUrl]);

  function onInput(e: ChangeEvent<HTMLInputElement>): void {
    setQuery(e.target.value);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    const href = fillTemplate(resultsHrefTemplate, { q: encodeURIComponent(trimmed) });
    if (typeof window !== 'undefined') window.location.href = href;
  }

  function onSuggestionKey(e: KeyboardEvent<HTMLLIElement>, slug: string): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goto(slug);
    }
  }

  function goto(slug: string): void {
    const href = fillTemplate(articleHrefTemplate, { slug: encodeURIComponent(slug) });
    if (typeof window !== 'undefined') window.location.href = href;
  }

  return (
    <BlockRoot as="section" style={heroStyle()} aria-labelledby="help-center-search-title">
      <div style={heroInnerStyle()}>
        <h1 {...part('title')} id="help-center-search-title" style={titleStyle()}>
          {headingText}
        </h1>
        {subtitle ? (
          <p {...part('subtitle')} style={subtitleStyle()}>
            {subtitle}
          </p>
        ) : null}
        <form role="search" onSubmit={onSubmit} style={formStyle()}>
          <input
            {...part('search')}
            type="search"
            value={query}
            onChange={onInput}
            placeholder={placeholderText}
            aria-label={headingText}
            autoComplete="off"
            style={inputStyle()}
          />
          <button {...part('submit')} type="submit" style={submitButtonStyle()}>
            {submitText}
          </button>
        </form>

        {showSuggestions && suggestions.length > 0 ? (
          <Card style={suggestionsWrapStyle()}>
            <ul
              role="listbox"
              aria-label={t('helpCenter.search.suggestions')}
              style={suggestionsListStyle()}
              {...part('list')}
            >
              {suggestions.map((row, idx) => {
                const slug = row.slug ?? '';
                const titleText =
                  localised(row.title) || slug || t('helpCenter.search.result', { index: idx + 1 });
                const excerptText = localised(row.excerpt);
                return (
                  <li
                    key={`${slug}-${idx}`}
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    onClick={() => slug && goto(slug)}
                    onKeyDown={(e) => slug && onSuggestionKey(e, slug)}
                    style={suggestionItemStyle()}
                    {...part('item', idx)}
                  >
                    <div style={suggestionTitleStyle()}>{titleText}</div>
                    {excerptText ? <div style={suggestionExcerptStyle()}>{excerptText}</div> : null}
                  </li>
                );
              })}
            </ul>
          </Card>
        ) : null}
        {showSuggestions && loading && query.trim().length >= 2 && suggestions.length === 0 ? (
          <div style={loadingStyle()}>{t('helpCenter.search.searching')}</div>
        ) : null}
      </div>
    </BlockRoot>
  );
}

function heroStyle(): CSSProperties {
  return {
    background: 'var(--color-bg-soft, #f6f7f9)',
    padding: 'var(--space-14, 56px) var(--space-4, 16px)',
  };
}

function heroInnerStyle(): CSSProperties {
  return {
    maxWidth: 720,
    margin: '0 auto',
    textAlign: 'center',
  };
}

function titleStyle(): CSSProperties {
  return {
    fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
    margin: '0 0 12px',
    color: 'var(--color-text, #1a1a1a)',
  };
}

function subtitleStyle(): CSSProperties {
  return {
    fontSize: 'var(--font-size-md, 16px)',
    color: 'var(--color-text-secondary, #555)',
    margin: '0 0 24px',
  };
}

function formStyle(): CSSProperties {
  return {
    display: 'flex',
    gap: 8,
    alignItems: 'stretch',
  };
}

function inputStyle(): CSSProperties {
  return {
    flex: 1,
    padding: 'var(--space-3-5, 14px) var(--space-5, 20px)',
    fontSize: 'var(--font-size-md, 16px)',
    borderRadius: 999,
    border: '1px solid var(--color-border, #d8d8d8)',
    background: 'var(--color-bg, #fff)',
    color: 'var(--color-text, #1a1a1a)',
    outline: 'none',
  };
}

function submitButtonStyle(): CSSProperties {
  return {
    padding: 'var(--space-3-5, 14px) var(--space-6, 24px)',
    fontSize: 'var(--font-size-base, 15px)',
    borderRadius: 999,
    border: 'none',
    background: 'var(--color-accent, #2156c4)',
    color: 'var(--color-on-accent, #fff)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

function suggestionsWrapStyle(): CSSProperties {
  return {
    ...cardReset,
    marginTop: 12,
    background: 'var(--color-bg, #fff)',
    borderRadius: 12,
    textAlign: 'left',
    overflow: 'hidden',
  };
}

function suggestionsListStyle(): CSSProperties {
  return {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    maxHeight: 360,
    overflowY: 'auto',
  };
}

function suggestionItemStyle(): CSSProperties {
  return {
    padding: 'var(--space-3, 12px) var(--space-4, 16px)',
    borderTop: '1px solid var(--color-border-soft, #f3f3f3)',
    cursor: 'pointer',
  };
}

function suggestionTitleStyle(): CSSProperties {
  return {
    fontSize: 'var(--font-size-base, 15px)',
    fontWeight: 500,
    color: 'var(--color-text, #1a1a1a)',
  };
}

function suggestionExcerptStyle(): CSSProperties {
  return {
    fontSize: '13px',
    color: 'var(--color-text-secondary, #666)',
    marginTop: 4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
}

function loadingStyle(): CSSProperties {
  return {
    marginTop: 12,
    fontSize: '13px',
    color: 'var(--color-text-tertiary, #888)',
  };
}
